import { useEffect, useMemo, useState } from "react";
import { conductAcknowledge, conductClear, conductDiscloseToHm, conductList, conductListMine, conductOpen, conductReroute, todayQueue, type ConductOpenOutput } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { Banner, Dialog, Empty, Field, KV, Pill, ReasonDialog, Receipt, Skeleton, fmtDate } from "@/ui";

// V1-INTERACTIONS §4.23 / POLICY §8.1 — Code of Conduct concerns at `#requisitions/<R>/concerns`. What is on this screen
// is decided by the server: the person it is routed to, its author and Admin get the record (P-COC.full); the owning
// Recruiter, the Hiring Manager and the named HR Business Partner get the process status and nothing else (P-COC.status);
// the person it is about is told nothing at all, so their screen is empty.

const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { open: "bad", routed: "warn", acknowledged: "warn", cleared: "ok" };
const STATUS_WORDS: Record<string, string> = {
  open: "Open",
  routed: "Routed",
  acknowledged: "Acknowledged",
  cleared: "Cleared",
};
const OUTCOMES: [string, string][] = [
  ["no_action", "No action"],
  ["note_on_record", "A note on the record"],
  ["candidate_withdrawn", "The candidate withdrew"],
  ["passed", "Passed on the candidate"],
];
const OUTCOME_WORDS = Object.fromEntries(OUTCOMES) as Record<string, string>;
const HANDLER_WORDS: Record<string, string> = { hrbp: "an HR Business Partner", admin: "an administrator", recruiter: "the Recruiter", hm: "the Hiring Manager" };
const SUBJECT_WORDS: Record<string, string> = { candidate: "the candidate", hiring_manager: "the Hiring Manager", recruiter: "the Recruiter", hrbp: "the HR Business Partner", other_person: "somebody else" };
const EVENT_WORDS: Record<string, string> = { raised: "Raised", routed: "Routed", acknowledged: "Acknowledged", cleared: "Cleared", outcome_recorded: "Outcome recorded", note_added: "Note" };

/** `conduct.list_mine` says how I am related to each concern: I raised it, it is routed to me, or both (POLICY §8.1).
 *  The person a concern is ABOUT is never in this list at all. */
const isRecipient = (r: { routed_to_me: boolean }) => r.routed_to_me;

export function ConcernsPage({ rid }: { rid: string }) {
  const { can } = useSession();
  const { register } = useViewState();
  // THE DURABLE ENTRY POINT (Codex #9 M10): the author of a concern and the person holding it reach it from their own
  // authorisation, not from a notification that ages out. Narrowed to this requisition and including cleared concerns,
  // so a handled one does not vanish from the screen that handled it.
  const mine = useRead(["concerns-mine", rid], () => conductListMine({ requisition_id: rid, include_cleared: true }), { retry: false, enabled: can("conduct.list_mine") });
  // the HR Business Partner's queue and the Admin's board: every open concern they may see, mine or not
  const listed = useRead(["concerns", rid], () => conductList({ requisition_id: rid, include_cleared: true }), { enabled: can("conduct.list"), retry: false });
  // somebody who is only TOLD a concern exists (the owning Recruiter, the Hiring Manager after disclosure) holds no
  // index of their own: the card that told them names it, so their id comes from their own Today
  const today = useRead(["today"], () => todayQueue({}), { retry: false });

  const index = useMemo(() => {
    const rows = mine.data?.items ?? [];
    const author: string[] = [];
    const recipient: string[] = [];
    for (const r of rows) (isRecipient(r) ? recipient : author).push(r.concern_id);
    const seen = new Set([...author, ...recipient]);
    const told: string[] = [];
    for (const i of listed.data?.items ?? []) {
      if (seen.has(i.concern_id)) continue;
      seen.add(i.concern_id);
      told.push(i.concern_id);
    }
    const rowsToday = today.data ? [...today.data.needs_you, ...today.data.overdue, ...today.data.waiting, ...today.data.upcoming, ...today.data.recent] : [];
    for (const r of rowsToday) {
      if (r.ref_type !== "concern" || !r.ref_id || seen.has(r.ref_id)) continue;
      if (!(r.route ?? "").includes(rid)) continue;
      seen.add(r.ref_id);
      told.push(r.ref_id);
    }
    return { author, recipient, told, all: Array.from(seen) };
  }, [mine.data, listed.data, today.data, rid]);

  useEffect(() => register({ view: "concerns", ids: { requisition: rid }, summary: `concerns on ${rid} · ${index.all.length}` }), [register, rid, index.all.length]);
  const loading = mine.isLoading || listed.isLoading || today.isLoading;
  // A FAILURE TO LOAD IS NOT AN EMPTY SCREEN (Codex #9 M10). A concealed concern and a broken read must never look the
  // same, so the index's own error is an error state with Retry and the problem's correlation id.
  const indexError = mine.isError ? mine.error : listed.isError ? listed.error : null;

  return (
    // A CONCERN IS NOT PRINTED (Codex #8 N02; V1-INTERACTIONS §5; POLICY §8.1). Every print this application makes is a
    // saved version with its receipt; ordinary browser Print of this screen would hand out an unreceipted copy of a
    // restricted record — including the verbatim "What was raised" — to anyone holding the paper. The card prints its
    // one-line explanation instead.
    <div className="screen-only">
      <p className="paper-only">A Code of Conduct concern is a restricted live record, read on screen. The application prints saved versions only (V1-INTERACTIONS §5).</p>
      <div className="hrow">
        <div>
          <h1 className="page">Code of Conduct · {rid}</h1>
          <p className="lede">Concerns you raised, concerns routed to you, and the process status you may read.</p>
        </div>
        <div className="acts">
          <a className="btn btn--sm" href={`#requisitions/${rid}`}>
            ← Requisition
          </a>
        </div>
      </div>
      {indexError && <Banner error={indexError} onRetry={() => void (mine.isError ? mine.refetch() : listed.refetch())} />}
      {loading && <Skeleton rows={3} />}
      {!loading && !indexError && index.all.length === 0 && (
        <Empty level={2} heading="No concerns are visible to you">
          {mine.data ? `You have raised no concern on this requisition${mine.data.routed_to_me === 0 ? ", and none is routed to you" : ""}.` : "Nothing here for you."}
          {can("conduct.list") ? " Nothing else on it is open to you either." : ""}
        </Empty>
      )}
      {!loading && !indexError && mine.data && (
        <>
          <h2 className="sec">You raised <span className="sec__n">{index.author.length}</span></h2>{index.author.length === 0 && <p className="muted">You have raised no concerns on this requisition.</p>}
          {index.author.map((id) => (
            <ConcernCard key={id} rid={rid} id={id} age={[...(mine.data?.items ?? []), ...(listed.data?.items ?? [])].find(row => row.concern_id === id)?.age_days} />
          ))}
        </>
      )}
      {!loading && !indexError && mine.data && (
        <>
          <h2 className="sec">Routed to you <span className="sec__n">{index.recipient.length}</span></h2>{index.recipient.length === 0 && <p className="muted">No concerns are routed to you on this requisition.</p>}
          {index.recipient.map((id) => (
            <ConcernCard key={id} rid={rid} id={id} age={[...(mine.data?.items ?? []), ...(listed.data?.items ?? [])].find(row => row.concern_id === id)?.age_days} />
          ))}
        </>
      )}
      {index.told.length > 0 && (
        <>
          {(index.author.length > 0 || index.recipient.length > 0) && <h2 className="sec">On this requisition</h2>}
          {index.told.map((id) => (
            <ConcernCard key={id} rid={rid} id={id} age={[...(mine.data?.items ?? []), ...(listed.data?.items ?? [])].find(row => row.concern_id === id)?.age_days} />
          ))}
        </>
      )}
    </div>
  );
}

type Dlg = null | { kind: "acknowledge" } | { kind: "clear" } | { kind: "reroute" } | { kind: "disclose" };

function ConcernCard({ rid, id, age }: { rid: string; id: string; age?: number | undefined }) {
  const q = useRead(["concern", id], () => conductOpen({ concern_id: id }), { retry: false });
  const inval = [["concern", id], ["concerns", rid], ["today"], ["debrief"], ["summary"]] as const;
  const acknowledge = useWrite((i: { concern_id: string; note?: string | null }, k) => conductAcknowledge(i, k), inval);
  const clear = useWrite((i: { concern_id: string; outcome: string; outcome_text: string }, k) => conductClear(i, k), inval);
  const reroute = useWrite((i: { concern_id: string; to_person_id: string; reason: string }, k) => conductReroute(i, k), inval);
  const disclose = useWrite((i: { concern_id: string; reason: string }, k) => conductDiscloseToHm(i, k), inval);
  const [dlg, setDlg] = useState<Dlg>(null);
  const [outcome, setOutcome] = useState("no_action");
  const [to, setTo] = useState("");
  if (q.isLoading) return <Skeleton rows={3} />;
  // 404 is the answer POLICY §8.1 gives the person a concern is ABOUT: nothing is rendered, not even that it exists.
  // Any other failure is a failure and says so with its correlation id, never a silent blank (Codex #9 M10).
  if (q.isError) return q.error.status === 404 ? null : <Banner error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const c: ConductOpenOutput = q.data;
  const can = ("can" in c ? c.can : {}) as Record<string, boolean>;
  const full = c.projection === "full";
  const err = acknowledge.error ?? clear.error ?? reroute.error ?? disclose.error;
  return (
    <section className="card concern">
      <div className="hrow">
        <h2 className="sec">
          Code of Conduct concern <Pill tone={STATUS_TONE[c.status] ?? "muted"}>{STATUS_WORDS[c.status] ?? c.status}</Pill>
          {age != null && <span className="muted"> · {c.status === "cleared" ? "raised" : "open"} {age} {age === 1 ? "business day" : "business days"}{c.status === "cleared" ? " ago" : ""}</span>}
        </h2>
        {full && (
          <div className="acts">
            {can["acknowledge"] && (
              <button className="btn btn--primary" onClick={() => setDlg({ kind: "acknowledge" })}>
                Acknowledge
              </button>
            )}
            {can["clear"] && (
              <button className={`btn${c.status === "acknowledged" ? " btn--primary" : ""}`} onClick={() => setDlg({ kind: "clear" })}>
                Clear
              </button>
            )}
            {can["disclose_to_hm"] && (
              <button className="btn" onClick={() => setDlg({ kind: "disclose" })}>
                Disclose to the Hiring Manager
              </button>
            )}
            {can["reroute"] && (
              <button className="btn" onClick={() => setDlg({ kind: "reroute" })}>
                Reroute
              </button>
            )}
          </div>
        )}
      </div>
      {c.blocks_debrief && <p className="muted">The debrief reveal waits on this.</p>}
      {clear.isSuccess && <Receipt>Cleared — the debrief reveal is released.</Receipt>}
      <Banner error={err} />
      {/* one list, so the columns line up: the status everybody with a relation is told, then the record itself */}
      <KV
        items={[
          ["Requisition", `${c.requisition_id} · ${c.requisition_title}`],
          ["Raised", fmtDate(c.raised_at, true)],
          ["Acknowledged", c.acknowledged_at ? fmtDate(c.acknowledged_at, true) : "Not yet"],
          ["Cleared", c.cleared_at ? fmtDate(c.cleared_at, true) : "Not yet"],
          ["Told the Hiring Manager", c.disclosed_to_hm_at ? `${fmtDate(c.disclosed_to_hm_at, true)}${full && c.disclosed_reason ? ` — ${c.disclosed_reason}` : ""}` : "Not yet"],
          ["Handled by", c.handled_by ? (HANDLER_WORDS[c.handled_by] ?? c.handled_by) : "nobody yet"],
          ["Outcome", c.outcome ? (OUTCOME_WORDS[c.outcome] ?? c.outcome) : "—"],
          ...(full
            ? ([
                ["About", c.subject_role ? (SUBJECT_WORDS[c.subject_role] ?? c.subject_role) + (c.subject_name ? ` — ${c.subject_name}` : "") : "—"],
                // POLICY §8.2: a candidate is named by their token on a screen like this one, never by name — and on a
                // requisition with more than one candidate the token is the only way to tell which application it is.
                ["Candidate", c.candidate_token ? `Candidate ${c.candidate_token.slice(-4).toUpperCase()}` : "—"],
                ["Raised by", c.raised_by ?? "—"],
                ["Routed to", c.routed_to ?? "—"],
                ["Round", c.round_label ?? "—"],
              ] as [string, string][])
            : []),
        ]}
      />
      {!full && <p className="muted">This is all you are told: a concern exists, where it stands and who is handling it. What it says stays with the person handling it.</p>}
      {full && (
        <>
          <h3 className="sub">What was raised</h3>
          <p className="muted">A concern is a restricted record. Only the person handling it, the person who raised it and an administrator read what it says; everybody else sees that it exists and where it stands.</p>
          <p className="prose restricted">{c.concern_text}</p>
          {c.outcome_text && (
            <>
              <h3 className="sub">What was decided</h3>
              <p className="prose restricted">{c.outcome_text}</p>
            </>
          )}
          {c.events && c.events.length > 0 && (
            <>
              <h3 className="sub">History</h3>
              <ul className="history">
                {c.events.map((e, i) => (
                  <li key={`${e.kind}-${e.at}-${i}`}>
                    <div className="note__header"><Pill category tone="muted">{EVENT_WORDS[e.kind] ?? e.kind}</Pill><span>{e.by}</span><time className="muted">{fmtDate(e.at, true)}</time></div>
                    {e.note ? <div className="wrap">{e.note}</div> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <ReasonDialog title="Acknowledge the concern" label="Note (optional)" confirm="Acknowledge" optional open={dlg?.kind === "acknowledge"} onClose={() => setDlg(null)} busy={acknowledge.isPending} error={acknowledge.error} onConfirm={(note) => void acknowledge.mutateAsync({ concern_id: id, ...(note ? { note } : {}) }).then(() => setDlg(null))}>
        <p className="muted">You have read it and you are handling it. The debrief stays blocked until it is cleared.</p>
      </ReasonDialog>
      <ReasonDialog title="Disclose to the Hiring Manager" label="Why they need to know now" confirm="Disclose" open={dlg?.kind === "disclose"} onClose={() => setDlg(null)} busy={disclose.isPending} error={disclose.error} onConfirm={(reason) => void disclose.mutateAsync({ concern_id: id, reason }).then(() => setDlg(null))}>
        <p className="muted">They are told the status only — never the words, never who it is about.</p>
      </ReasonDialog>
      <Dialog title="Clear the concern" open={dlg?.kind === "clear"} onClose={() => setDlg(null)}>
        <ClearForm
          outcome={outcome}
          setOutcome={setOutcome}
          busy={clear.isPending}
          error={clear.error}
          onCancel={() => setDlg(null)}
          onConfirm={(text) => void clear.mutateAsync({ concern_id: id, outcome, outcome_text: text }).then(() => setDlg(null))}
        />
      </Dialog>
      <Dialog title="Reroute the concern" open={dlg?.kind === "reroute"} onClose={() => setDlg(null)}>
        <RerouteForm
          to={to}
          setTo={setTo}
          busy={reroute.isPending}
          error={reroute.error}
          onCancel={() => setDlg(null)}
          onConfirm={(reason) => void reroute.mutateAsync({ concern_id: id, to_person_id: to.trim(), reason }).then(() => setDlg(null))}
        />
      </Dialog>
    </section>
  );
}

function ClearForm({ outcome, setOutcome, busy, error, onCancel, onConfirm }: { outcome: string; setOutcome: (v: string) => void; busy: boolean; error: Parameters<typeof Banner>[0]["error"]; onCancel: () => void; onConfirm: (text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <>
      <Field label="Outcome" required id="cc-outcome">
        <select id="cc-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          {OUTCOMES.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="What you decided" required id="cc-text" hint="Stays with you and the administrators; the Summary Sheet shows the outcome only.">
        <textarea id="cc-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} />
      </Field>
      <Banner error={error} />
      <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || text.trim().length < 3} onClick={() => onConfirm(text.trim())}>
          {busy ? "Saving…" : "Clear the concern"}
        </button>
      </div>
    </>
  );
}

function RerouteForm({ to, setTo, busy, error, onCancel, onConfirm }: { to: string; setTo: (v: string) => void; busy: boolean; error: Parameters<typeof Banner>[0]["error"]; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <>
      <Field label="Hand it to" required id="cc-to" hint="An active HR Business Partner or administrator who is not on this requisition, not the person it is about and not the person who raised it.">
        <input id="cc-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="p_hrbp_2" />
      </Field>
      <Field label="Why" required id="cc-why">
        <textarea id="cc-why" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
      </Field>
      <Banner error={error} />
      <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || to.trim().length < 2 || reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>
          {busy ? "Saving…" : "Reroute"}
        </button>
      </div>
    </>
  );
}
